import React, { useState } from 'react';
import { Button } from './ui/button';
import { Upload, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ImageUploader = ({ onUploadComplete, multiple = false, existingImages = [] }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedUrls, setUploadedUrls] = useState(existingImages);

  const uploadToCloudinary = async (file) => {
    const sigResponse = await axios.get(`${BACKEND_URL}/api/cloudinary/signature`);
    const { signature, timestamp, cloud_name, api_key, folder } = sigResponse.data;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('api_key', api_key);
    formData.append('timestamp', timestamp);
    formData.append('signature', signature);
    formData.append('folder', folder);

    const uploadResponse = await axios.post(
      `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
      formData
    );
    return uploadResponse.data.secure_url;
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const newUrls = [];

    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} is not an image file`);
          continue;
        }
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is too large (max 10MB)`);
          continue;
        }

        const url = await uploadToCloudinary(file);
        newUrls.push(url);
        toast.success(`${file.name} uploaded successfully`);
      }

      const allUrls = multiple ? [...uploadedUrls, ...newUrls] : newUrls;
      setUploadedUrls(allUrls);
      onUploadComplete(multiple ? allUrls : newUrls[0]);
    } catch (error) {
      toast.error('Upload failed. Please check Cloudinary configuration.');
      console.error(error);
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const removeImage = (urlToRemove) => {
    const newUrls = uploadedUrls.filter(url => url !== urlToRemove);
    setUploadedUrls(newUrls);
    onUploadComplete(multiple ? newUrls : '');
  };

  return (
    <div className="space-y-4">
      <div>
        <input
          type="file"
          id="image-upload"
          accept="image/*"
          multiple={multiple}
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading}
        />
        <label htmlFor="image-upload">
          <Button
            type="button"
            onClick={() => document.getElementById('image-upload').click()}
            disabled={uploading}
            className="btn-secondary w-full"
            asChild
          >
            <span>
              {uploading ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={18} />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="mr-2" size={18} />
                  {multiple ? 'Upload Images' : 'Upload Image'}
                </>
              )}
            </span>
          </Button>
        </label>
        <p className="text-xs text-gray-400 mt-2">
          {multiple ? 'Select multiple images (max 10MB each)' : 'Select an image (max 10MB)'}
        </p>
      </div>

      {uploadedUrls.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {uploadedUrls.map((url, index) => (
            <div key={index} className="relative group">
              <img
                src={url}
                alt={`Upload ${index + 1}`}
                className="w-full h-24 object-cover rounded-lg border border-gold-500/30"
              />
              <button
                type="button"
                onClick={() => removeImage(url)}
                className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
